<template>
  <section :class="['driver-tree-view', { 'driver-tree-view--collapsed': !props.panelOpen }]">
    <div class="driver-tree-view__toolbar">
      <va-input
        v-model="filter"
        placeholder="Filtrer un conducteur, une agence..."
        clearable
        class="driver-tree-view__filter"
      />
      <button
        type="button"
        class="driver-tree-view__toggle"
        :aria-label="props.panelOpen ? 'Replier le panneau' : 'Deplier le panneau'"
        @click="emit('toggle-panel')"
      >
        {{ props.panelOpen ? "x" : ">" }}
      </button>
    </div>

    <div class="driver-tree-view__body">
      <p v-if="loading" class="driver-tree-view__state">
        Chargement...
      </p>
      <p v-else-if="error" class="driver-tree-view__state driver-tree-view__state--error">
        {{ error }}
      </p>
      <p v-else-if="!hasResults" class="driver-tree-view__state">
        Aucun resultat pour ce filtre.
      </p>

      <ul v-else class="driver-tree-view__list">
        <tree-item
          class="item"
          :item="filteredTreeModel"
          :default-open="Boolean(filter.trim())"
          @select="selection"
        />
      </ul>
    </div>

    <div class="driver-tree-view__footer">
      <button
        type="button"
        class="driver-tree-view__refresh"
        :aria-label="loading || refreshing ? 'Rafraichissement en cours' : 'Rafraichir l arborescence'"
        :disabled="loading || refreshing"
        @click="refreshTree"
      >
        <img
          :class="['driver-tree-view__refresh-icon', { 'driver-tree-view__refresh-icon--spinning': loading || refreshing }]"
          :src="reloadIcon"
          alt=""
        >
      </button>
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from "vue";
import userService from "@/services/user.service";
import TreeItem from "./TreeItem.vue";
import reloadIcon from "@/../assets/reload.png";

const props = defineProps({
  panelOpen: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(["select", "toggle-panel"]);
const filter = ref("");
const selected = ref(null);
const loading = ref(true);
const refreshing = ref(false);
const error = ref("");
const treeModel = ref({
  name: "Tableau de bord",
  id: "0",
  type: "home",
  children: []
});

defineExpose({
  selected
});

function normalizeText(value) {
  return (value || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

function getCompanyFromUser(user) {
  return user.Company || {
    idCompany: user.company,
    companyName: user.companyName
  };
}

function getFirstAgencyFromUser(user) {
  const agencies = user.Agencies || [];
  if (!agencies.length) {
    return null;
  }

  const candidate = agencies[0];
  return candidate.Agency || candidate;
}

function sortNodes(nodes = []) {
  nodes.sort((left, right) => left.name.localeCompare(right.name, "fr", { sensitivity: "base" }));
  nodes.forEach(node => {
    if (Array.isArray(node.children) && node.children.length) {
      sortNodes(node.children);
    }
  });
  return nodes;
}

function buildTree(users = []) {
  const root = {
    name: "Tableau de bord",
    id: "0",
    type: "home",
    children: []
  };
  const companiesById = new Map();

  users
    .filter(user => user?.Role?.nameRole === "driver")
    .forEach(user => {
      const company = getCompanyFromUser(user);
      if (!company?.idCompany) {
        return;
      }

      let companyNode = companiesById.get(company.idCompany);
      if (!companyNode) {
        companyNode = {
          name: company.companyName || "Societe",
          id: `company-${company.idCompany}`,
          type: "company",
          obj: company,
          children: []
        };
        companyNode._agencies = new Map();
        companiesById.set(company.idCompany, companyNode);
      }

      const agency = getFirstAgencyFromUser(user);
      const agencyKey = agency?.idAgency ? `agency-${agency.idAgency}` : `agency-none-${company.idCompany}`;
      let agencyNode = companyNode._agencies.get(agencyKey);

      if (!agencyNode) {
        agencyNode = {
          name: agency?.agencyName || "Sans agence",
          id: agencyKey,
          type: agency ? "agency" : "none",
          obj: agency,
          children: []
        };
        companyNode._agencies.set(agencyKey, agencyNode);
        companyNode.children.push(agencyNode);
      }

      agencyNode.children.push({
        name: [user.username, user.name, user.surname].filter(Boolean).join(" - "),
        id: user.username,
        type: "driver",
        selected: false,
        obj: user
      });
    });

  root.children = Array.from(companiesById.values()).map(companyNode => {
    delete companyNode._agencies;
    return companyNode;
  });

  return {
    ...root,
    children: sortNodes(root.children)
  };
}

function filterNode(node, query) {
  if (!query) {
    return node;
  }

  const children = (node.children || [])
    .map(child => filterNode(child, query))
    .filter(Boolean);
  const matches = normalizeText(node.name).includes(query);

  if (matches || children.length || node.type === "home") {
    return {
      ...node,
      children
    };
  }

  return null;
}

const filteredTreeModel = computed(() => {
  const query = normalizeText(filter.value.trim());
  return filterNode(treeModel.value, query) || { ...treeModel.value, children: [] };
});

const hasResults = computed(() => (filteredTreeModel.value.children || []).length > 0);

const selection = (item) => {
  if (selected.value != null) {
    selected.value.selected = false;
  }

  item.selected = true;
  selected.value = item;
  emit("select", item);
};

async function loadTree({ initial = false } = {}) {
  if (initial) {
    loading.value = true;
  }
  else {
    refreshing.value = true;
  }

  error.value = "";

  try {
    const response = await userService.get();
    treeModel.value = buildTree(response.data || []);
  }
  catch (e) {
    error.value = "Impossible de charger l'arborescence.";
  }
  finally {
    if (initial) {
      loading.value = false;
    }
    else {
      refreshing.value = false;
    }
  }
}

const refreshTree = async () => {
  await loadTree();
};

await loadTree({ initial: true });
</script>

<style scoped>
.driver-tree-view {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  gap: 0;
  min-height: 100%;
  height: 100%;
  position: relative;
}

.driver-tree-view__body {
  min-height: 0;
  overflow-y: auto;
  background: #ffffff;
}

.driver-tree-view__toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 32px;
  align-items: stretch;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  position: relative;
}

.driver-tree-view__filter {
  width: 100%;
  margin: 0;
  padding: 12px 0 12px 16px;
}

.driver-tree-view__filter :deep(.va-input-wrapper__container),
.driver-tree-view__filter :deep(.va-input-wrapper),
.driver-tree-view__filter :deep(.va-input__content) {
  border-radius: 0 !important;
  box-shadow: none !important;
}

.driver-tree-view__toggle {
  width: 32px;
  border: 0;
  border-left: 1px solid #e5e7eb;
  border-radius: 0;
  background: #ffffff;
  color: #4b5563;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease;
}

.driver-tree-view__toggle:hover {
  background: #f8fafc;
  color: #111827;
}

.driver-tree-view--collapsed .driver-tree-view__toolbar {
  display: none;
}

.driver-tree-view--collapsed .driver-tree-view__body,
.driver-tree-view--collapsed .driver-tree-view__footer {
  display: none;
}

.driver-tree-view__state {
  margin: 0;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  color: #4b5563;
  font-size: 0.9rem;
}

.driver-tree-view__state--error {
  background: #fff8ed;
  color: #92400e;
}

.driver-tree-view__list {
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: #ffffff;
  overflow: visible;
}

.item {
  cursor: pointer;
}

.item > ul {
  padding: 0 0 0 0.85rem !important;
  line-height: 1.35em;
}

.driver-tree-view__footer {
  padding: 0.75rem;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
}

.driver-tree-view__refresh {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 0;
  background: #f8fafc;
  padding: 0.58rem 0.85rem;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.driver-tree-view__refresh:hover:not(:disabled) {
  background: #eef2f7;
  border-color: #cbd5e1;
}

.driver-tree-view__refresh:disabled {
  cursor: wait;
  opacity: 0.7;
}

.driver-tree-view__refresh-icon {
  width: 18px;
  height: 18px;
  object-fit: contain;
}

.driver-tree-view__refresh-icon--spinning {
  animation: tree-refresh-spin 0.9s linear infinite;
}

@keyframes tree-refresh-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
